import { createFileRoute, redirect } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useEffect, useMemo, useState } from "react";
import type { ChangeEvent, FormEvent, MouseEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { customInstanceBpm } from "../../api/axios-client";
import { useAuth } from "../../auth";
import ListLayout from "../../components/Layout/ListLayout";

type EmailProcessListItem = {
  id?: string;
  routeId?: string;
  processDefinitionId?: string;
  processDefinitionKey?: string;
  processDefinitionTitle?: string;
  emailAddress?: string;
  imapHost?: string;
  imapPort?: number;
  imapUseSsl?: boolean;
  imapUseStarttls?: boolean;
  smtpHost?: string;
  smtpPort?: number;
  smtpUseSsl?: boolean;
  smtpUseStarttls?: boolean;
  pollingIntervalSeconds?: number;
  startTag?: string;
  autoReplyText?: string;
  mailboxStatus?: "OK" | "AUTHENTICATION_FAILED" | string;
  enabled: boolean;
};

type EmailProcessGroupedItem = EmailProcessListItem & {
  routes: EmailProcessListItem[];
  processBindings: ProcessBindingFormState[];
};

type ProcessBindingFormState = {
  clientId: string;
  routeId?: string;
  processDefinitionKey: string;
  startTag: string;
};

type EmailProcessFormState = {
  processBindings: ProcessBindingFormState[];
  provider: EmailProvider;
  emailAddress: string;
  imapHost: string;
  imapPort: string;
  imapUseSsl: boolean;
  imapUseStarttls: boolean;
  smtpHost: string;
  smtpPort: string;
  smtpUseSsl: boolean;
  smtpUseStarttls: boolean;
  secret: string;
  pollingEnabled: boolean;
  pollingIntervalSeconds: string;
  autoReplyText: string;
};

type EmailProvider = "gmail" | "hotmail" | "microsoft" | "other";

type ProviderSettings = {
  imapHost: string;
  imapPort: string;
  imapUseSsl: boolean;
  imapUseStarttls: boolean;
  smtpHost: string;
  smtpPort: string;
  smtpUseSsl: boolean;
  smtpUseStarttls: boolean;
};

type ProcessDefinitionOption = {
  id: string;
  key?: string;
  name?: string;
};

type ProcessedEmailItem = {
  id?: string;
  routeId?: string;
  emailId?: string;
  subject?: string;
  status?: string;
  processInstanceId?: string;
  processDefinitionKey?: string;
  startedAt?: string;
  error?: string;
};

type PageResponse<T> = {
  content?: T[];
  totalElements?: number;
};

const processDisplayName = (
  processDefinition?: Pick<
    ProcessDefinitionOption,
    "id" | "key" | "name"
  > | null,
) => {
  return (
    processDefinition?.name ||
    processDefinition?.key ||
    processDefinition?.id ||
    "-"
  );
};

const providerOptions: Array<{
  value: EmailProvider;
  label?: string;
  labelKey?: string;
}> = [
  { value: "gmail", label: "Gmail" },
  { value: "hotmail", label: "Outlook/Hotmail" },
  { value: "microsoft", label: "Microsoft 365" },
  { value: "other", labelKey: "emailProcessConfigurations.providerCustom" },
];

const providerSettings: Record<
  Exclude<EmailProvider, "other">,
  ProviderSettings
> = {
  gmail: {
    imapHost: "imap.gmail.com",
    imapPort: "993",
    imapUseSsl: true,
    imapUseStarttls: false,
    smtpHost: "smtp.gmail.com",
    smtpPort: "587",
    smtpUseSsl: false,
    smtpUseStarttls: true,
  },
  hotmail: {
    imapHost: "outlook.office365.com",
    imapPort: "993",
    imapUseSsl: true,
    imapUseStarttls: false,
    smtpHost: "smtp-mail.outlook.com",
    smtpPort: "587",
    smtpUseSsl: false,
    smtpUseStarttls: true,
  },
  microsoft: {
    imapHost: "outlook.office365.com",
    imapPort: "993",
    imapUseSsl: true,
    imapUseStarttls: false,
    smtpHost: "smtp.office365.com",
    smtpPort: "587",
    smtpUseSsl: false,
    smtpUseStarttls: true,
  },
};

const emptyProviderSettings: ProviderSettings = {
  imapHost: "",
  imapPort: "",
  imapUseSsl: false,
  imapUseStarttls: false,
  smtpHost: "",
  smtpPort: "",
  smtpUseSsl: false,
  smtpUseStarttls: false,
};

const emailProcessSearchSchema = z.object({
  page: z.number().catch(1),
  size: z.number().catch(10),
  q: z.string().optional(),
});

const createProcessBinding = (
  processDefinitionKey = "",
  startTag = "",
  routeId?: string,
): ProcessBindingFormState => ({
  clientId: `${Date.now()}-${Math.random()}`,
  routeId,
  processDefinitionKey,
  startTag,
});

const createEmptyForm = (): EmailProcessFormState => ({
  processBindings: [createProcessBinding()],
  provider: "gmail",
  emailAddress: "",
  ...providerSettings.gmail,
  secret: "",
  pollingEnabled: false,
  pollingIntervalSeconds: "300",
  autoReplyText: "",
});

const groupEmailProcessConfigurations = (
  items: EmailProcessListItem[],
): EmailProcessGroupedItem[] => {
  const groups = new Map<string, EmailProcessGroupedItem>();

  items.forEach((item) => {
    const key = [
      item.emailAddress,
      item.imapHost,
      item.imapPort,
      item.imapUseSsl,
      item.imapUseStarttls,
      item.smtpHost,
      item.smtpPort,
      item.smtpUseSsl,
      item.smtpUseStarttls,
      item.pollingIntervalSeconds,
      item.autoReplyText,
    ]
      .map((value) => String(value ?? ""))
      .join("|");

    const current = groups.get(key);
    const binding = createProcessBinding(
      item.processDefinitionKey || "",
      item.startTag || "",
      item.routeId,
    );

    if (!current) {
      groups.set(key, {
        ...item,
        enabled: Boolean(item.enabled),
        routes: [item],
        processBindings: [binding],
      });
      return;
    }

    current.routes.push(item);
    current.processBindings.push(binding);
    current.enabled = current.routes.some((route) => route.enabled);
    if (item.mailboxStatus === "AUTHENTICATION_FAILED") {
      current.mailboxStatus = item.mailboxStatus;
    }
  });

  return Array.from(groups.values());
};

type EmailProcessSearch = z.infer<typeof emailProcessSearchSchema>;

export const Route = createFileRoute("/__auth/email-process-configurations")({
  component: EmailProcessConfigurationsPage,
  beforeLoad: ({ context }) => {
    if (context.auth.isLoading) return;
    if (
      !context.auth.user?.isAdmin &&
      !context.auth.hasPermission("emailprocess.view")
    ) {
      throw redirect({ to: "/dashboard" });
    }
  },
  validateSearch: (search) => emailProcessSearchSchema.parse(search),
  staticData: {
    title: "routes.emailProcessConfigurations",
  },
});

function EmailProcessConfigurationsPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user, currentTenant, hasPermission } = useAuth();
  const canCreate = Boolean(user?.isAdmin || hasPermission("emailprocess.new"));
  const canEdit = Boolean(user?.isAdmin || hasPermission("emailprocess.edit"));
  const canDelete = Boolean(
    user?.isAdmin || hasPermission("emailprocess.delete"),
  );
  const canEnable = Boolean(
    user?.isAdmin || hasPermission("emailprocess.enable"),
  );
  const canDisable = Boolean(
    user?.isAdmin || hasPermission("emailprocess.disable"),
  );
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const search = Route.useSearch() as EmailProcessSearch;
  const [anchorPosition, setAnchorPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [selected, setSelected] = useState<EmailProcessGroupedItem | null>(
    null,
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRouteId, setEditingRouteId] = useState<string | null>(null);
  const [processedEmailsOpen, setProcessedEmailsOpen] = useState(false);
  const [processedEmailsItem, setProcessedEmailsItem] =
    useState<EmailProcessGroupedItem | null>(null);
  const [processedEmailsPage, setProcessedEmailsPage] = useState(0);
  const [processedEmailsSize, setProcessedEmailsSize] = useState(10);
  const [processedEmailsSearch, setProcessedEmailsSearch] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteRouteIds, setDeleteRouteIds] = useState<string[]>([]);
  const [removedProcessBindingRouteIds, setRemovedProcessBindingRouteIds] =
    useState<string[]>([]);
  const [form, setForm] = useState<EmailProcessFormState>(createEmptyForm);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const basePath = currentTenant
    ? `/v1/${currentTenant}/email-process/configurations`
    : "";

  const queryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String((search.page || 1) - 1));
    params.set("size", String(search.size || 10));
    return params.toString();
  }, [search]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["email-process-configurations", currentTenant, queryParams],
    queryFn: () =>
      customInstanceBpm<PageResponse<EmailProcessListItem>>(
        `${basePath}?${queryParams}`,
        { method: "GET" },
      ),
    enabled: !!user?.id && !!currentTenant,
  });

  const {
    data: processDefinitions = [],
    isFetching: isFetchingProcessDefinitions,
    error: processDefinitionsError,
    refetch: refetchProcessDefinitions,
  } = useQuery({
    queryKey: ["email-process-definition-options", currentTenant],
    queryFn: () =>
      customInstanceBpm<ProcessDefinitionOption[]>(
        `${basePath}/process-definitions`,
        { method: "GET" },
      ),
    enabled: !!user?.id && !!currentTenant,
  });

  const processedEmailRouteIds = useMemo(
    () =>
      (processedEmailsItem?.routes || [])
        .map((route) => route.routeId)
        .filter(Boolean) as string[],
    [processedEmailsItem],
  );

  const processedEmailsParams = useMemo(() => {
    const params = new URLSearchParams();
    processedEmailRouteIds.forEach((routeId) =>
      params.append("routeIds", routeId),
    );
    params.set("page", String(processedEmailsPage));
    params.set("size", String(processedEmailsSize));
    if (processedEmailsSearch.trim())
      params.set("q", processedEmailsSearch.trim());
    return params.toString();
  }, [
    processedEmailRouteIds,
    processedEmailsPage,
    processedEmailsSearch,
    processedEmailsSize,
  ]);

  const {
    data: processedEmailsData,
    isLoading: isProcessedEmailsLoading,
    error: processedEmailsError,
  } = useQuery({
    queryKey: [
      "email-process-processed-emails",
      currentTenant,
      processedEmailsParams,
    ],
    queryFn: () =>
      customInstanceBpm<PageResponse<ProcessedEmailItem>>(
        `${basePath}/processed-emails?${processedEmailsParams}`,
        { method: "GET" },
      ),
    enabled:
      processedEmailsOpen &&
      !!user?.id &&
      !!currentTenant &&
      processedEmailRouteIds.length > 0,
  });

  const reprocessEmailMutation = useMutation({
    mutationFn: (controlId: string) =>
      customInstanceBpm(`${basePath}/processed-emails/${controlId}/reprocess`, {
        method: "POST",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["email-process-processed-emails"],
      });
      setSnackbar({
        open: true,
        severity: "success",
        message: t(
          "emailProcessConfigurations.processedEmails.reprocessSuccess",
        ),
      });
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        severity: "error",
        message: getErrorMessage(
          error,
          t("emailProcessConfigurations.processedEmails.reprocessError"),
        ),
      });
    },
  });

  const items = (data?.content || []).map((item) => ({
    ...item,
    routeId: item.routeId || item.id,
  }));
  const groupedItems = useMemo(
    () => groupEmailProcessConfigurations(items),
    [items],
  );
  const filteredItems = useMemo(() => {
    const query = (search.q || "").trim().toLowerCase();
    if (!query) return groupedItems;
    return groupedItems.filter((item) =>
      [
        item.processDefinitionTitle,
        item.processDefinitionKey,
        item.processDefinitionId,
        item.emailAddress,
        item.imapHost,
        item.startTag,
        ...item.processBindings.flatMap((binding) => [
          binding.processDefinitionKey,
          binding.startTag,
        ]),
        ...item.routes.flatMap((route) => [
          route.processDefinitionTitle,
          route.processDefinitionKey,
          route.processDefinitionId,
          route.startTag,
        ]),
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(query),
      ),
    );
  }, [groupedItems, search.q]);

  const invalidateConfigurations = () => {
    queryClient.invalidateQueries({
      queryKey: ["email-process-configurations"],
    });
  };

  const currentMailboxTestSignature = useMemo(
    () => getMailboxTestSignature(form),
    [form],
  );
  const [mailboxTestSignature, setMailboxTestSignature] = useState<
    string | null
  >(null);
  const isMailboxTested = mailboxTestSignature === currentMailboxTestSignature;

  const statusMutation = useMutation({
    mutationFn: ({
      routeIds,
      enabled,
    }: {
      routeIds: string[];
      enabled: boolean;
    }) =>
      Promise.all(
        routeIds.map((routeId) =>
          customInstanceBpm<void>(
            `${basePath}/${routeId}/${enabled ? "enable" : "disable"}`,
            { method: "PATCH" },
          ),
        ),
      ),
    onSuccess: (_, variables) => {
      invalidateConfigurations();
      setSnackbar({
        open: true,
        severity: "success",
        message: variables.enabled
          ? t("emailProcessConfigurations.enableSuccess")
          : t("emailProcessConfigurations.disableSuccess"),
      });
    },
    onError: (error) => {
      showError(
        getErrorMessage(
          error,
          t("emailProcessConfigurations.updateError"),
        ),
      );
    },
  });

  const testMailboxMutation = useMutation({
    mutationFn: async (signature: string) => {
      const testMailboxUrl = editingRouteId
        ? `${basePath}/${editingRouteId}/test-mailbox`
        : `${basePath}/test-mailbox`;
      const response = await customInstanceBpm<{
        success?: boolean;
        message?: string;
      }>(testMailboxUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(toMailboxTestPayload(form)),
      });
      return { response, signature };
    },
    onSuccess: ({ signature }) => {
      setMailboxTestSignature(signature);
      setSnackbar({
        open: true,
        severity: "success",
        message: t("emailProcessConfigurations.testMailboxSuccess"),
      });
    },
    onError: (error) => {
      setMailboxTestSignature(null);
      showError(
        getErrorMessage(
          error,
          t("emailProcessConfigurations.testMailboxError"),
        ),
      );
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = toPayload(form);
      const response = await customInstanceBpm<EmailProcessListItem>(
        editingRouteId ? `${basePath}/${editingRouteId}` : basePath,
        {
          method: editingRouteId ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (editingRouteId && removedProcessBindingRouteIds.length) {
        await Promise.all(
          removedProcessBindingRouteIds.map((routeId) =>
            customInstanceBpm<void>(`${basePath}/${routeId}`, {
              method: "DELETE",
            }),
          ),
        );
      }

      return response;
    },
    onSuccess: () => {
      invalidateConfigurations();
      closeDialog();
      setSnackbar({
        open: true,
        severity: "success",
        message: editingRouteId
          ? t("emailProcessConfigurations.editSuccess")
          : t("emailProcessConfigurations.createSuccess"),
      });
    },
    onError: (error) => {
      showError(
        getErrorMessage(
          error,
          t("emailProcessConfigurations.saveError"),
        ),
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (routeIds: string[]) =>
      Promise.all(
        routeIds.map((routeId) =>
          customInstanceBpm<void>(`${basePath}/${routeId}`, {
            method: "DELETE",
          }),
        ),
      ),
    onSuccess: () => {
      invalidateConfigurations();
      setSnackbar({
        open: true,
        severity: "success",
        message: t("emailProcessConfigurations.deleteSuccess"),
      });
    },
    onError: (error) => {
      showError(
        getErrorMessage(
          error,
          t("emailProcessConfigurations.deleteError"),
        ),
      );
    },
  });

  const showError = (message: string) => {
    setSnackbar({
      open: true,
      severity: "error",
      message,
    });
  };

  const handleMenuOpen = (
    event: MouseEvent<HTMLButtonElement>,
    item: EmailProcessGroupedItem,
  ) => {
    event.stopPropagation();
    setAnchorPosition({
      top: event.clientY,
      left: event.clientX,
    });
    setSelected(item);
  };

  const handleMenuClose = () => {
    setAnchorPosition(null);
    setSelected(null);
  };

  const openCreateDialog = () => {
    setEditingRouteId(null);
    setRemovedProcessBindingRouteIds([]);
    setMailboxTestSignature(null);
    setForm(createEmptyForm());
    setDialogOpen(true);
    if (!processDefinitions.length) {
      refetchProcessDefinitions();
    }
  };

  const openEditDialog = () => {
    if (!selected?.routeId) return;
    setEditingRouteId(selected.routeId);
    setRemovedProcessBindingRouteIds([]);
    setMailboxTestSignature(null);
    setForm(toForm(selected));
    setDialogOpen(true);
    if (!processDefinitions.length) {
      refetchProcessDefinitions();
    }
    handleMenuClose();
  };

  const openProcessedEmailsDialog = () => {
    if (!selected?.routeId) return;
    setProcessedEmailsItem(selected);
    setProcessedEmailsPage(0);
    setProcessedEmailsSearch("");
    setProcessedEmailsOpen(true);
    handleMenuClose();
  };

  const closeProcessedEmailsDialog = () => {
    setProcessedEmailsOpen(false);
    setProcessedEmailsItem(null);
    setProcessedEmailsPage(0);
    setProcessedEmailsSearch("");
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingRouteId(null);
    setRemovedProcessBindingRouteIds([]);
    setMailboxTestSignature(null);
    setForm(createEmptyForm());
  };

  const handleToggleStatus = () => {
    if (!selected?.routeId) return;
    const routeIds = selected.routes
      .map((route) => route.routeId)
      .filter(Boolean) as string[];
    statusMutation.mutate({
      routeIds,
      enabled: !selected.enabled,
    });
    handleMenuClose();
  };

  const handleDelete = () => {
    if (!selected?.routeId) return;
    const routeIds = selected.routes
      .map((route) => route.routeId)
      .filter(Boolean) as string[];
    handleMenuClose();
    setDeleteRouteIds(routeIds);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (deleteMutation.isPending) return;
    setDeleteConfirmOpen(false);
    setDeleteRouteIds([]);
  };

  const confirmDelete = () => {
    if (!deleteRouteIds.length) return;
    deleteMutation.mutate(deleteRouteIds, {
      onSettled: () => {
        setDeleteConfirmOpen(false);
        setDeleteRouteIds([]);
      },
    });
  };

  const handleFilter = (newFilters: { q?: string }) => {
    (navigate as any)({
      search: {
        ...search,
        ...newFilters,
        page: 1,
      },
    });
  };

  const handleTextChange =
    (field: keyof EmailProcessFormState) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleProviderChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const provider = event.target.value as EmailProvider;
    setForm((prev) => ({
      ...prev,
      provider,
      ...(provider === "other"
        ? emptyProviderSettings
        : providerSettings[provider]),
    }));
  };

  const handleCheckboxChange =
    (field: keyof EmailProcessFormState) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      setForm((prev) => ({
        ...prev,
        [field]: checked,
        ...(field === "pollingEnabled" && !checked
          ? { pollingIntervalSeconds: "300" }
          : {}),
      }));
    };

  const handleProcessBindingChange =
    (
      clientId: string,
      field: keyof Omit<ProcessBindingFormState, "clientId" | "routeId">,
    ) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({
        ...prev,
        processBindings: prev.processBindings.map((binding) =>
          binding.clientId === clientId
            ? { ...binding, [field]: event.target.value }
            : binding,
        ),
      }));
    };

  const addProcessBinding = () => {
    setForm((prev) => ({
      ...prev,
      processBindings: [...prev.processBindings, createProcessBinding()],
    }));
  };

  const removeProcessBinding = (clientId: string) => {
    setForm((prev) => ({
      ...prev,
      processBindings:
        prev.processBindings.length > 1
          ? prev.processBindings.filter(
              (binding) => binding.clientId !== clientId,
            )
          : prev.processBindings,
    }));
    const routeId = form.processBindings.find(
      (binding) => binding.clientId === clientId,
    )?.routeId;
    if (routeId) {
      setRemovedProcessBindingRouteIds((prev) =>
        prev.includes(routeId) ? prev : [...prev, routeId],
      );
    }
  };

  const handleTestMailbox = () => {
    const validationError = getMailboxTestValidationError(
      form,
      Boolean(editingRouteId),
      t,
    );
    if (validationError) {
      showError(validationError);
      return;
    }
    testMailboxMutation.mutate(currentMailboxTestSignature);
  };

  const handleSave = () => {
    const validationError = getEmailProcessValidationError(
      form,
      Boolean(editingRouteId),
      t,
    );
    if (validationError) {
      showError(validationError);
      return;
    }

    const selectedBindings = form.processBindings.filter(
      (binding) => binding.processDefinitionKey,
    );
    if (
      selectedBindings.length > 1 &&
      selectedBindings.some((binding) => !binding.startTag.trim())
    ) {
      showError(
        t("emailProcessConfigurations.validation.subaddressRequiredMultiple"),
      );
      return;
    }
    const subaddresses = selectedBindings
      .map((binding) => binding.startTag.trim().toLowerCase())
      .filter(Boolean);
    if (new Set(subaddresses).size !== subaddresses.length) {
      showError(
        t("emailProcessConfigurations.validation.uniqueSubaddresses"),
      );
      return;
    }
    if (!editingRouteId && !form.secret) {
      showError(
        t("emailProcessConfigurations.validation.tokenRequiredToCreate"),
      );
      return;
    }
    if ((toNumber(form.pollingIntervalSeconds) || 0) < 60) {
      showError(
        t("emailProcessConfigurations.validation.minPollingInterval"),
      );
      return;
    }
    if (!isMailboxTested) {
      showError(
        t("emailProcessConfigurations.validation.testMailboxFirst"),
      );
      return;
    }
    saveMutation.mutate();
  };

  const isMutating =
    statusMutation.isPending ||
    saveMutation.isPending ||
    deleteMutation.isPending ||
    testMailboxMutation.isPending;

  return (
    <>
      <ListLayout
        title={t("emailProcessConfigurations.title")}
        subtitle={t(
          "emailProcessConfigurations.emailProcessConfigurationsSubtitle",
        )}
        filters={
          <EmailProcessConfigurationFilters
            onApply={handleFilter}
            isLoading={isLoading}
            initialValues={{ q: search.q }}
          />
        }
        actions={
          canCreate ? (
            <IconButton color="primary" onClick={openCreateDialog}>
              <AddIcon />
            </IconButton>
          ) : undefined
        }
        error={error}
        isLoading={isLoading}
        isEmpty={!filteredItems.length}
        emptyMessage={t("emailProcessConfigurations.emptyMessage")}
      >
        {isMobile ? (
          <Stack spacing={2}>
            {filteredItems.map((item, index) => (
              <Paper
                key={`${item.routeId || item.processDefinitionId || item.emailAddress || "email-process"}-${index}`}
                sx={{ p: 2 }}
              >
                <Stack spacing={1}>
                  <ProcessBindingsSummary item={item} />
                  <Typography variant="body2" color="text.secondary">
                    {t("emailProcessConfigurations.email")}: {item.emailAddress || "-"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("emailProcessConfigurations.provider")}: {formatProviderLabel(item, t)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("emailProcessConfigurations.subaddress")}: {formatProcessSubaddresses(item)}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={
                        item.enabled ? t("common.active") : t("common.inactive")
                      }
                      size="small"
                      color={item.enabled ? "success" : "default"}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {t("emailProcessConfigurations.processCount", {
                        count: item.processBindings.length,
                      })}
                    </Typography>
                  </Stack>
                  <IconButton
                    size="small"
                    disabled={!item.routeId || isMutating}
                    onClick={(event) => handleMenuOpen(event, item)}
                    sx={{ alignSelf: "flex-end" }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <EmailProcessConfigurationsTable
            items={filteredItems}
            onMenuOpen={handleMenuOpen}
            isUpdating={isMutating}
          />
        )}

        <TablePagination
          component="div"
          count={filteredItems.length}
          page={(search.page || 1) - 1}
          onPageChange={(_, newPage) => {
            (navigate as any)({
              search: { ...search, page: newPage + 1 },
              replace: true,
            });
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          rowsPerPage={search.size || 10}
          onRowsPerPageChange={(event) => {
            (navigate as any)({
              search: {
                ...search,
                size: parseInt(event.target.value, 10),
                page: 1,
              },
              replace: true,
            });
          }}
          labelRowsPerPage={t("common.rowsPerPage")}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} ${t("common.of")} ${count}`
          }
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            mt: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            mb: 8,
          }}
        />
        <Menu
          open={Boolean(anchorPosition)}
          onClose={handleMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={anchorPosition || undefined}
          elevation={3}
        >
          {canEdit && (
            <MenuItem
              onClick={openEditDialog}
              disabled={!selected?.routeId || isMutating}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" sx={{ color: "text.secondary" }} />
              </ListItemIcon>
              <ListItemText>{t("common.edit")}</ListItemText>
            </MenuItem>
          )}
          {((selected?.enabled && canDisable) ||
            (!selected?.enabled && canEnable)) && (
            <MenuItem
              onClick={handleToggleStatus}
              disabled={!selected?.routeId || isMutating}
            >
              <ListItemIcon>
                {selected?.enabled ? (
                  <VisibilityOffIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                ) : (
                  <CheckCircleIcon
                    fontSize="small"
                    sx={{ color: "text.secondary" }}
                  />
                )}
              </ListItemIcon>
              <ListItemText>
                {selected?.enabled
                  ? t("emailProcessConfigurations.disableAction")
                  : t("emailProcessConfigurations.enableAction")}
              </ListItemText>
            </MenuItem>
          )}
          <MenuItem
            onClick={openProcessedEmailsDialog}
            disabled={!selected?.routeId}
          >
            <ListItemIcon>
              <MarkEmailReadIcon
                fontSize="small"
                sx={{ color: "text.secondary" }}
              />
            </ListItemIcon>
            <ListItemText>
              {t("emailProcessConfigurations.processedEmails.title")}
            </ListItemText>
          </MenuItem>
          {canDelete && (
            <MenuItem
              onClick={handleDelete}
              disabled={!selected?.routeId || isMutating}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
              </ListItemIcon>
              <ListItemText>{t("common.delete")}</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </ListLayout>
      <EmailProcessConfigurationDialog
        open={dialogOpen}
        title={
          editingRouteId
            ? t("emailProcessConfigurations.dialog.editTitle")
            : t("emailProcessConfigurations.dialog.createTitle")
        }
        form={form}
        processDefinitions={processDefinitions}
        isLoadingProcessDefinitions={isFetchingProcessDefinitions}
        processDefinitionsError={processDefinitionsError}
        onRetryProcessDefinitions={() => refetchProcessDefinitions()}
        isSaving={saveMutation.isPending}
        isTestingMailbox={testMailboxMutation.isPending}
        isMailboxTested={isMailboxTested}
        isEditing={Boolean(editingRouteId)}
        onClose={closeDialog}
        onSave={handleSave}
        onTestMailbox={handleTestMailbox}
        onTextChange={handleTextChange}
        onProviderChange={handleProviderChange}
        onCheckboxChange={handleCheckboxChange}
        onProcessBindingChange={handleProcessBindingChange}
        onAddProcessBinding={addProcessBinding}
        onRemoveProcessBinding={removeProcessBinding}
      />
      <ProcessedEmailsDialog
        open={processedEmailsOpen}
        item={processedEmailsItem}
        emails={processedEmailsData?.content || []}
        total={processedEmailsData?.totalElements || 0}
        page={processedEmailsPage}
        rowsPerPage={processedEmailsSize}
        search={processedEmailsSearch}
        isLoading={isProcessedEmailsLoading}
        error={processedEmailsError}
        onClose={closeProcessedEmailsDialog}
        onPageChange={setProcessedEmailsPage}
        onRowsPerPageChange={(rowsPerPage) => {
          setProcessedEmailsSize(rowsPerPage);
          setProcessedEmailsPage(0);
        }}
        onSearchChange={(value) => {
          setProcessedEmailsSearch(value);
          setProcessedEmailsPage(0);
        }}
        onReprocess={(email) =>
          email.id && reprocessEmailMutation.mutate(email.id)
        }
        isReprocessing={reprocessEmailMutation.isPending}
        canReprocess={canEdit}
      />
      <Dialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t("emailProcessConfigurations.deleteDialog.title")}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("emailProcessConfigurations.deleteDialog.message")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeDeleteConfirm}
            disabled={deleteMutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleteMutation.isPending}
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

function EmailProcessConfigurationFilters({
  onApply,
  isLoading,
  initialValues,
}: {
  onApply: (values: { q?: string }) => void;
  isLoading?: boolean;
  initialValues?: { q?: string };
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState(initialValues?.q || "");

  useEffect(() => {
    setQuery(initialValues?.q || "");
  }, [initialValues?.q]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onApply({ q: query });
  };

  const handleClear = () => {
    setQuery("");
    onApply({ q: "" });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mb: 1 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid size={{ xs: 12, md: "grow" }}>
          <TextField
            fullWidth
            label={t("emailProcessConfigurations.searchLabel")}
            placeholder={t("emailProcessConfigurations.searchPlaceholder")}
            size="small"
            variant="outlined"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid size="auto" sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClear}
            disabled={isLoading}
            sx={{ minWidth: 80 }}
          >
            {t("common.clear")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            disabled={isLoading}
            sx={{ minWidth: 100, fontWeight: 600 }}
          >
            {t("common.search")}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

function ProcessedEmailsDialog({
  open,
  item,
  emails,
  total,
  page,
  rowsPerPage,
  search,
  isLoading,
  error,
  onClose,
  onPageChange,
  onRowsPerPageChange,
  onSearchChange,
  onReprocess,
  isReprocessing,
  canReprocess,
}: {
  open: boolean;
  item: EmailProcessGroupedItem | null;
  emails: ProcessedEmailItem[];
  total: number;
  page: number;
  rowsPerPage: number;
  search: string;
  isLoading: boolean;
  error: unknown;
  onClose: () => void;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSearchChange: (value: string) => void;
  onReprocess: (email: ProcessedEmailItem) => void;
  isReprocessing: boolean;
  canReprocess: boolean;
}) {
  const { t } = useTranslation();
  const [actionsAnchor, setActionsAnchor] = useState<HTMLElement | null>(null);
  const [selectedEmail, setSelectedEmail] = useState<ProcessedEmailItem | null>(
    null,
  );
  const [detailsEmail, setDetailsEmail] = useState<ProcessedEmailItem | null>(
    null,
  );
  const actionsOpen = Boolean(actionsAnchor);
  const selectedEmailFailed = isFailureProcessedEmailStatus(
    selectedEmail?.status,
  );

  const closeActions = () => {
    setActionsAnchor(null);
    setSelectedEmail(null);
  };

  const openActions = (
    event: MouseEvent<HTMLButtonElement>,
    email: ProcessedEmailItem,
  ) => {
    setActionsAnchor(event.currentTarget);
    setSelectedEmail(email);
  };

  const handleReprocess = () => {
    if (selectedEmail && selectedEmailFailed) {
      onReprocess(selectedEmail);
    }
    closeActions();
  };

  const handleDetails = () => {
    if (selectedEmail && selectedEmailFailed) {
      setDetailsEmail(selectedEmail);
    }
    closeActions();
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle>
          {t("emailProcessConfigurations.processedEmails.title")}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "flex-end" }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2">
                  {item?.emailAddress || "-"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatProcessSubaddresses(item || undefined)}
                </Typography>
              </Box>
              <TextField
                size="small"
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder={t(
                  "emailProcessConfigurations.processedEmails.search",
                )}
                sx={{ width: { xs: "100%", sm: 320 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            {error ? (
              <Alert severity="error">
                {t("emailProcessConfigurations.processedEmails.loadError")}
              </Alert>
            ) : null}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("emailProcessConfigurations.processedEmails.emailId")}
                    </TableCell>
                    <TableCell>
                      {t("emailProcessConfigurations.processedEmails.subject")}
                    </TableCell>
                    <TableCell>
                      {t("emailProcessConfigurations.processedEmails.date")}
                    </TableCell>
                    <TableCell align="center">
                      {t("emailProcessConfigurations.processedEmails.status")}
                    </TableCell>
                    <TableCell align="center">
                      {t("emailProcessConfigurations.actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            "emailProcessConfigurations.processedEmails.loading",
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : emails.length ? (
                    emails.map((email) => {
                      const failed = isFailureProcessedEmailStatus(
                        email.status,
                      );
                      return (
                        <TableRow
                          key={`${email.id || email.routeId}-${email.startedAt}-${email.emailId}`}
                        >
                          <TableCell
                            sx={{ maxWidth: 280, wordBreak: "break-word" }}
                          >
                            {email.emailId || "-"}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 320 }}>
                            <Typography
                              variant="body2"
                              noWrap
                              title={email.subject || ""}
                            >
                              {email.subject || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {formatDateTime(email.startedAt)}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={formatProcessedEmailStatus(
                                email.status,
                                t,
                              )}
                              size="small"
                              sx={{
                                fontWeight: 600,
                                borderRadius: 2,
                                ...getProcessedEmailStatusChipSx(email.status),
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={(event) => openActions(event, email)}
                              disabled={!failed || isReprocessing}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">
                          {t(
                            "emailProcessConfigurations.processedEmails.empty",
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => onPageChange(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) =>
                onRowsPerPageChange(parseInt(event.target.value, 10))
              }
              labelRowsPerPage={t(
                "emailProcessConfigurations.processedEmails.rowsPerPage",
              )}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} ${t("common.of")} ${count}`
              }
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            {t("emailProcessConfigurations.close")}
          </Button>
        </DialogActions>
      </Dialog>
      <Menu
        anchorEl={actionsAnchor}
        open={actionsOpen}
        onClose={closeActions}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {canReprocess && (
          <MenuItem
            onClick={handleReprocess}
            disabled={
              !selectedEmail?.id || !selectedEmailFailed || isReprocessing
            }
          >
            <ListItemText>
              {t("emailProcessConfigurations.processedEmails.reprocess")}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleDetails} disabled={!selectedEmailFailed}>
          <ListItemText>
            {t("emailProcessConfigurations.processedEmails.details")}
          </ListItemText>
        </MenuItem>
      </Menu>
      <Dialog
        open={Boolean(detailsEmail)}
        onClose={() => setDetailsEmail(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t(resolveFailureDetail(detailsEmail).titleKey)}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="error">
              {t(resolveFailureDetail(detailsEmail).messageKey)}
            </Alert>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t("emailProcessConfigurations.processedEmails.status")}
              </Typography>
              <Typography variant="body2">
                {formatProcessedEmailStatus(detailsEmail?.status, t)}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsEmail(null)}>
            {t("emailProcessConfigurations.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function EmailProcessConfigurationDialog({
  open,
  title,
  form,
  processDefinitions,
  isLoadingProcessDefinitions,
  processDefinitionsError,
  onRetryProcessDefinitions,
  isSaving,
  isTestingMailbox,
  isMailboxTested,
  isEditing,
  onClose,
  onSave,
  onTestMailbox,
  onTextChange,
  onProviderChange,
  onCheckboxChange,
  onProcessBindingChange,
  onAddProcessBinding,
  onRemoveProcessBinding,
}: {
  open: boolean;
  title: string;
  form: EmailProcessFormState;
  processDefinitions: ProcessDefinitionOption[];
  isLoadingProcessDefinitions: boolean;
  processDefinitionsError: unknown;
  onRetryProcessDefinitions: () => void;
  isSaving: boolean;
  isTestingMailbox: boolean;
  isMailboxTested: boolean;
  isEditing: boolean;
  onClose: () => void;
  onSave: () => void;
  onTestMailbox: () => void;
  onTextChange: (
    field: keyof EmailProcessFormState,
  ) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onProviderChange: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onCheckboxChange: (
    field: keyof EmailProcessFormState,
  ) => (event: ChangeEvent<HTMLInputElement>) => void;
  onProcessBindingChange: (
    clientId: string,
    field: keyof Omit<ProcessBindingFormState, "clientId" | "routeId">,
  ) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onAddProcessBinding: () => void;
  onRemoveProcessBinding: (clientId: string) => void;
}) {
  const { t } = useTranslation();
  const requiresSubaddress = form.processBindings.length > 1;
  const showManualServerFields = form.provider === "other";
  const hasProcessDefinitionOptions = processDefinitions.length > 0;
  const hasProcessDefinitionsError = Boolean(processDefinitionsError);

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Stack spacing={2}>
            {form.processBindings.map((binding) => (
              <Stack
                key={binding.clientId}
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "flex-start" }}
              >
                <TextField
                  select
                  label={t("emailProcessConfigurations.dialog.linkedProcess")}
                  value={binding.processDefinitionKey}
                  onChange={onProcessBindingChange(
                    binding.clientId,
                    "processDefinitionKey",
                  )}
                  required
                  fullWidth
                  disabled={
                    isLoadingProcessDefinitions && !hasProcessDefinitionOptions
                  }
                  error={hasProcessDefinitionsError}
                  helperText={
                    hasProcessDefinitionsError
                      ? t(
                          "emailProcessConfigurations.dialog.loadProcessesError",
                        )
                      : undefined
                  }
                >
                  {isLoadingProcessDefinitions &&
                    !hasProcessDefinitionOptions && (
                      <MenuItem value="" disabled>
                        {t("common.loading")}
                      </MenuItem>
                    )}
                  {!isLoadingProcessDefinitions &&
                    !hasProcessDefinitionsError &&
                    !hasProcessDefinitionOptions && (
                      <MenuItem value="" disabled>
                        {t(
                          "emailProcessConfigurations.dialog.noProcessesFound",
                        )}
                      </MenuItem>
                    )}
                  {processDefinitions.map((processDefinition) => (
                    <MenuItem
                      key={processDefinition.key || processDefinition.id}
                      value={processDefinition.key || processDefinition.id}
                    >
                      {processDisplayName(processDefinition)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label={t("emailProcessConfigurations.dialog.subaddress")}
                  value={binding.startTag}
                  onChange={onProcessBindingChange(
                    binding.clientId,
                    "startTag",
                  )}
                  required={requiresSubaddress}
                  helperText={
                    requiresSubaddress
                      ? t("emailProcessConfigurations.dialog.subaddressHelp")
                      : undefined
                  }
                  fullWidth
                />
                <IconButton
                  aria-label={t(
                    "emailProcessConfigurations.dialog.removeProcess",
                  )}
                  color="error"
                  disabled={form.processBindings.length === 1 || isSaving}
                  onClick={() => onRemoveProcessBinding(binding.clientId)}
                  sx={{ mt: { md: 1 } }}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onAddProcessBinding}
              disabled={
                isSaving ||
                (isLoadingProcessDefinitions && !hasProcessDefinitionOptions)
              }
              sx={{ alignSelf: "flex-start" }}
            >
              {t("emailProcessConfigurations.dialog.addProcess")}
            </Button>
            {hasProcessDefinitionsError && (
              <Button
                variant="text"
                onClick={onRetryProcessDefinitions}
                disabled={isLoadingProcessDefinitions}
                sx={{ alignSelf: "flex-start" }}
              >
                {t("emailProcessConfigurations.dialog.reloadProcesses")}
              </Button>
            )}
          </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label={t("emailProcessConfigurations.dialog.monitoredEmail")}
              value={form.emailAddress}
              onChange={onTextChange("emailAddress")}
              required
              fullWidth
            />
            <TextField
              label={t("emailProcessConfigurations.dialog.token")}
              type="password"
              value={form.secret}
              onChange={onTextChange("secret")}
              placeholder={isEditing ? "••••••••" : undefined}
              helperText={
                isEditing
                  ? t("emailProcessConfigurations.dialog.tokenHelp")
                  : undefined
              }
              required={!isEditing}
              fullWidth
            />
          </Stack>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              select
              label={t("emailProcessConfigurations.dialog.provider")}
              value={form.provider}
              onChange={onProviderChange}
              required
              fullWidth
            >
              {providerOptions.map((provider) => (
                <MenuItem key={provider.value} value={provider.value}>
                  {formatProviderOptionLabel(provider, t)}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.pollingEnabled}
                  onChange={onCheckboxChange("pollingEnabled")}
                />
              }
              label={t("emailProcessConfigurations.dialog.polling")}
              sx={{ minWidth: { md: 140 } }}
            />
          </Stack>
          {showManualServerFields && (
            <>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label={t("emailProcessConfigurations.dialog.imap")}
                  value={form.imapHost}
                  onChange={onTextChange("imapHost")}
                  required
                  fullWidth
                />
                <TextField
                  label={t("emailProcessConfigurations.dialog.imapPort")}
                  type="number"
                  value={form.imapPort}
                  onChange={onTextChange("imapPort")}
                  required
                  sx={{ minWidth: { md: 160 } }}
                />
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.imapUseSsl}
                      onChange={onCheckboxChange("imapUseSsl")}
                    />
                  }
                  label={t("emailProcessConfigurations.dialog.imapSsl")}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.imapUseStarttls}
                      onChange={onCheckboxChange("imapUseStarttls")}
                    />
                  }
                  label={t("emailProcessConfigurations.dialog.imapStarttls")}
                />
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label={t("emailProcessConfigurations.dialog.smtp")}
                  value={form.smtpHost}
                  onChange={onTextChange("smtpHost")}
                  required
                  fullWidth
                />
                <TextField
                  label={t("emailProcessConfigurations.dialog.smtpPort")}
                  type="number"
                  value={form.smtpPort}
                  onChange={onTextChange("smtpPort")}
                  required
                  sx={{ minWidth: { md: 160 } }}
                />
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.smtpUseSsl}
                      onChange={onCheckboxChange("smtpUseSsl")}
                    />
                  }
                  label={t("emailProcessConfigurations.dialog.smtpSsl")}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.smtpUseStarttls}
                      onChange={onCheckboxChange("smtpUseStarttls")}
                    />
                  }
                  label={t("emailProcessConfigurations.dialog.smtpStarttls")}
                />
              </Stack>
            </>
          )}
          {form.pollingEnabled && (
            <TextField
              label={t("emailProcessConfigurations.dialog.pollingInterval")}
              type="number"
              value={form.pollingIntervalSeconds}
              onChange={onTextChange("pollingIntervalSeconds")}
              inputProps={{ min: 60 }}
              helperText={t(
                "emailProcessConfigurations.dialog.pollingIntervalHelp",
              )}
              required
              fullWidth
            />
          )}
          <TextField
            label={t("emailProcessConfigurations.dialog.autoReplyText")}
            value={form.autoReplyText}
            onChange={onTextChange("autoReplyText")}
            multiline
            minRows={4}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={
            isTestingMailbox ? (
              <CircularProgress color="inherit" size={16} />
            ) : isMailboxTested ? (
              <CheckCircleIcon />
            ) : (
              <MarkEmailReadIcon />
            )
          }
          color={isMailboxTested ? "success" : "primary"}
          onClick={onTestMailbox}
          disabled={isSaving || isTestingMailbox}
        >
          {isTestingMailbox
            ? t("emailProcessConfigurations.dialog.testingMailbox")
            : t("emailProcessConfigurations.dialog.testMailbox")}
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={isSaving || isTestingMailbox}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={isSaving || isTestingMailbox || !isMailboxTested}
          >
            {t("common.save")}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

function EmailProcessConfigurationsTable({
  items,
  onMenuOpen,
  isUpdating,
}: {
  items: EmailProcessGroupedItem[];
  onMenuOpen: (
    event: MouseEvent<HTMLButtonElement>,
    item: EmailProcessGroupedItem,
  ) => void;
  isUpdating: boolean;
}) {
  const { t } = useTranslation();

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        boxShadow: (muiTheme) => muiTheme.shadows[2],
        borderRadius: 3,
        overflow: "auto",
      }}
    >
      <Table sx={{ minWidth: 980 }}>
        <TableHead sx={{ bgcolor: "grey.50" }}>
          <TableRow>
            <HeaderCell>{t("emailProcessConfigurations.process")}</HeaderCell>
            <HeaderCell>{t("emailProcessConfigurations.email")}</HeaderCell>
            <HeaderCell>{t("emailProcessConfigurations.provider")}</HeaderCell>
            <HeaderCell>
              {t("emailProcessConfigurations.subaddress")}
            </HeaderCell>
            <HeaderCell align="center">
              {t("emailProcessConfigurations.status")}
            </HeaderCell>
            <HeaderCell align="center">
              {t("emailProcessConfigurations.actions")}
            </HeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item) => (
            <TableRow
              key={
                item.routeId ||
                `${item.processDefinitionId || item.emailAddress}-${item.startTag || "catch-all"}`
              }
              sx={{
                bgcolor: "background.paper",
                "&:hover": { bgcolor: "grey.50" },
                transition: "all 0.1s",
                height: 72,
              }}
            >
              <TableCell>
                <ProcessBindingsSummary item={item} />
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {item.emailAddress || "-"}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {formatProviderLabel(item, t)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" color="text.secondary">
                  {formatProcessSubaddresses(item)}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <EmailProcessStatusChip item={item} />
              </TableCell>
              <TableCell align="center">
                <IconButton
                  size="small"
                  disabled={!item.routeId || isUpdating}
                  onClick={(event) => onMenuOpen(event, item)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function EmailProcessStatusChip({ item }: { item: EmailProcessGroupedItem }) {
  const { t } = useTranslation();
  const hasAuthenticationFailure =
    item.mailboxStatus === "AUTHENTICATION_FAILED" ||
    item.routes.some(
      (route) => route.mailboxStatus === "AUTHENTICATION_FAILED",
    );

  const label = hasAuthenticationFailure
    ? t("emailProcessConfigurations.authenticationFailed")
    : item.enabled
      ? t("common.active")
      : t("common.inactive");

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        fontWeight: 600,
        borderRadius: 2,
        bgcolor: hasAuthenticationFailure
          ? "error.light"
          : item.enabled
            ? "success.light"
            : "grey.100",
        color: hasAuthenticationFailure
          ? "error.dark"
          : item.enabled
            ? "success.dark"
            : "text.primary",
        border: "none",
      }}
    />
  );
}

function ProcessBindingsSummary({ item }: { item: EmailProcessGroupedItem }) {
  return (
    <Stack spacing={0.75}>
      {item.routes.map((route) => (
        <Box
          key={
            route.routeId ||
            `${route.processDefinitionId || route.processDefinitionKey}-${route.startTag || "default"}`
          }
        >
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {processDisplayName({
              id: route.processDefinitionId || "",
              key: route.processDefinitionKey,
              name: route.processDefinitionTitle,
            })}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {route.processDefinitionId || "-"}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}

function HeaderCell({
  children,
  align = "left",
}: {
  children: ReactNode;
  align?: "left" | "center";
}) {
  return (
    <TableCell
      align={align}
      sx={{
        fontWeight: 700,
        color: "text.secondary",
        fontSize: "0.75rem",
        textTransform: "uppercase",
        letterSpacing: 0,
      }}
    >
      {children}
    </TableCell>
  );
}

function toForm(item: EmailProcessGroupedItem): EmailProcessFormState {
  const provider = inferProvider(item);
  const automaticSettings =
    provider === "other" ? null : providerSettings[provider];

  return {
    processBindings: item.processBindings.length
      ? item.processBindings.map((binding) =>
          createProcessBinding(
            binding.processDefinitionKey,
            binding.startTag,
            binding.routeId,
          ),
        )
      : [
          createProcessBinding(
            item.processDefinitionKey || "",
            item.startTag || "",
          ),
        ],
    provider,
    emailAddress: item.emailAddress || "",
    imapHost: automaticSettings?.imapHost || item.imapHost || "",
    imapPort: automaticSettings?.imapPort || String(item.imapPort || 993),
    imapUseSsl: automaticSettings?.imapUseSsl ?? item.imapUseSsl ?? true,
    imapUseStarttls:
      automaticSettings?.imapUseStarttls ?? Boolean(item.imapUseStarttls),
    smtpHost: automaticSettings?.smtpHost || item.smtpHost || "",
    smtpPort: automaticSettings?.smtpPort || String(item.smtpPort || 587),
    smtpUseSsl: automaticSettings?.smtpUseSsl ?? Boolean(item.smtpUseSsl),
    smtpUseStarttls:
      automaticSettings?.smtpUseStarttls ?? item.smtpUseStarttls ?? true,
    secret: "",
    pollingEnabled: Boolean(
      item.pollingIntervalSeconds && item.pollingIntervalSeconds !== 300,
    ),
    pollingIntervalSeconds: String(item.pollingIntervalSeconds || 300),
    autoReplyText: item.autoReplyText || "",
  };
}

function toPayload(form: EmailProcessFormState) {
  const serverSettings =
    form.provider === "other" ? form : providerSettings[form.provider];
  const processBindings = form.processBindings
    .filter((binding) => binding.processDefinitionKey)
    .map((binding) => ({
      routeId: binding.routeId || null,
      processDefinitionKey: binding.processDefinitionKey,
      startTag: binding.startTag.trim() || null,
    }));
  const firstBinding = processBindings[0] || {
    processDefinitionKey: "",
    startTag: null,
  };

  return {
    processDefinitionKey: firstBinding.processDefinitionKey,
    processBindings,
    emailAddress: form.emailAddress,
    imapHost: serverSettings.imapHost,
    imapPort: toNumber(serverSettings.imapPort),
    imapUseSsl: serverSettings.imapUseSsl,
    imapUseStarttls: serverSettings.imapUseStarttls,
    smtpHost: serverSettings.smtpHost || null,
    smtpPort: toNumber(serverSettings.smtpPort),
    smtpUseSsl: serverSettings.smtpUseSsl,
    smtpUseStarttls: serverSettings.smtpUseStarttls,
    secret: form.secret || null,
    pollingIntervalSeconds: toNumber(form.pollingIntervalSeconds),
    startTag: firstBinding.startTag,
    autoReplyText: form.autoReplyText || null,
  };
}

function toMailboxTestPayload(form: EmailProcessFormState) {
  const serverSettings =
    form.provider === "other" ? form : providerSettings[form.provider];

  return {
    emailAddress: form.emailAddress,
    imapHost: serverSettings.imapHost,
    imapPort: toNumber(serverSettings.imapPort),
    imapUseSsl: serverSettings.imapUseSsl,
    imapUseStarttls: serverSettings.imapUseStarttls,
    smtpHost: serverSettings.smtpHost || null,
    smtpPort: toNumber(serverSettings.smtpPort),
    smtpUseSsl: serverSettings.smtpUseSsl,
    smtpUseStarttls: serverSettings.smtpUseStarttls,
    username: form.emailAddress,
    secret: form.secret || null,
  };
}

function getMailboxTestSignature(form: EmailProcessFormState) {
  return JSON.stringify(toMailboxTestPayload(form));
}

function getMailboxTestValidationError(
  form: EmailProcessFormState,
  isEditing: boolean,
  t: (key: string, options?: Record<string, any>) => string,
) {
  const serverSettings =
    form.provider === "other" ? form : providerSettings[form.provider];

  if (!form.emailAddress.trim()) {
    return t("emailProcessConfigurations.validation.monitoredEmailRequired");
  }

  if (!isEditing && !form.secret.trim()) {
    return t("emailProcessConfigurations.validation.tokenRequiredToTest");
  }

  if (!serverSettings.imapHost.trim()) {
    return t("emailProcessConfigurations.validation.imapRequired");
  }

  if (!serverSettings.imapPort.trim()) {
    return t("emailProcessConfigurations.validation.imapPortRequired");
  }

  if (toNumber(serverSettings.imapPort) === null) {
    return t("emailProcessConfigurations.validation.imapPortNumeric");
  }

  if (!serverSettings.smtpHost.trim()) {
    return t("emailProcessConfigurations.validation.smtpRequired");
  }

  if (!serverSettings.smtpPort.trim()) {
    return t("emailProcessConfigurations.validation.smtpPortRequired");
  }

  if (toNumber(serverSettings.smtpPort) === null) {
    return t("emailProcessConfigurations.validation.smtpPortNumeric");
  }

  return null;
}

function getEmailProcessValidationError(
  form: EmailProcessFormState,
  isEditing: boolean,
  t: (key: string, options?: Record<string, any>) => string,
) {
  const selectedBindings = form.processBindings.filter((binding) =>
    binding.processDefinitionKey.trim(),
  );
  const firstEmptyProcessIndex = form.processBindings.findIndex(
    (binding) => !binding.processDefinitionKey.trim(),
  );
  const serverSettings =
    form.provider === "other" ? form : providerSettings[form.provider];

  if (!selectedBindings.length) {
    return t("emailProcessConfigurations.validation.linkedProcessRequired");
  }

  if (firstEmptyProcessIndex >= 0) {
    return t(
      "emailProcessConfigurations.validation.linkedProcessRequiredIndex",
      { index: firstEmptyProcessIndex + 1 },
    );
  }

  if (selectedBindings.length > 1) {
    const emptySubaddressIndex = form.processBindings.findIndex(
      (binding) => !binding.startTag.trim(),
    );
    if (emptySubaddressIndex >= 0) {
      return t(
        "emailProcessConfigurations.validation.subaddressRequiredIndex",
        { index: emptySubaddressIndex + 1 },
      );
    }
  }

  if (!form.emailAddress.trim()) {
    return t("emailProcessConfigurations.validation.monitoredEmailRequired");
  }

  if (!isEditing && !form.secret.trim()) {
    return t("emailProcessConfigurations.validation.tokenRequired");
  }

  if (!form.provider) {
    return t("emailProcessConfigurations.validation.providerRequired");
  }

  if (!serverSettings.imapHost.trim()) {
    return t("emailProcessConfigurations.validation.imapRequired");
  }

  if (!serverSettings.imapPort.trim()) {
    return t("emailProcessConfigurations.validation.imapPortRequired");
  }

  if (toNumber(serverSettings.imapPort) === null) {
    return t("emailProcessConfigurations.validation.imapPortNumeric");
  }

  if (!serverSettings.smtpHost.trim()) {
    return t("emailProcessConfigurations.validation.smtpRequired");
  }

  if (!serverSettings.smtpPort.trim()) {
    return t("emailProcessConfigurations.validation.smtpPortRequired");
  }

  if (toNumber(serverSettings.smtpPort) === null) {
    return t("emailProcessConfigurations.validation.smtpPortNumeric");
  }

  if (!form.pollingIntervalSeconds.trim()) {
    return t("emailProcessConfigurations.validation.pollingIntervalRequired");
  }

  const pollingIntervalSeconds = toNumber(form.pollingIntervalSeconds);
  if (pollingIntervalSeconds === null) {
    return t("emailProcessConfigurations.validation.pollingIntervalNumeric");
  }

  if (pollingIntervalSeconds < 60) {
    return t("emailProcessConfigurations.validation.minPollingInterval");
  }

  return null;
}

function inferProvider(item: EmailProcessListItem): EmailProvider {
  const imapHost = (item.imapHost || "").toLowerCase();
  const smtpHost = (item.smtpHost || "").toLowerCase();
  if (
    imapHost === providerSettings.gmail.imapHost &&
    smtpHost === providerSettings.gmail.smtpHost
  ) {
    return "gmail";
  }
  if (
    imapHost === providerSettings.hotmail.imapHost &&
    smtpHost === providerSettings.hotmail.smtpHost
  ) {
    return "hotmail";
  }
  if (
    imapHost === providerSettings.microsoft.imapHost &&
    smtpHost === providerSettings.microsoft.smtpHost
  ) {
    return "microsoft";
  }
  return "other";
}

function formatProviderOptionLabel(
  option: (typeof providerOptions)[number],
  t: (key: string) => string,
) {
  return option.labelKey ? t(option.labelKey) : option.label || "-";
}

function formatProviderLabel(
  item: EmailProcessListItem,
  t: (key: string) => string,
) {
  const provider = providerOptions.find(
    (option) => option.value === inferProvider(item),
  );
  return provider ? formatProviderOptionLabel(provider, t) : "-";
}

function toNumber(value: string) {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatHostPort(host?: string, port?: number) {
  if (!host && !port) return "-";
  if (!port) return host || "-";
  if (!host) return String(port);
  return `${host}:${port}`;
}

function formatProcessSubaddresses(item?: EmailProcessGroupedItem) {
  const values = (item?.routes || [])
    .map((route) => route.startTag)
    .filter(Boolean);
  return values.length ? values.join(", ") : "-";
}

function formatProcessedEmailStatus(
  status: string | undefined,
  t: (key: string) => string,
) {
  if (status === "FINALIZADO")
    return t("emailProcessConfigurations.processedEmails.statuses.finished");
  if (status === "EM_ANDAMENTO")
    return t("emailProcessConfigurations.processedEmails.statuses.inProgress");
  if (status === "PUBLISHING")
    return t("emailProcessConfigurations.processedEmails.statuses.publishing");
  if (status === "PUBLISHED")
    return t("emailProcessConfigurations.processedEmails.statuses.published");
  if (status === "PROCESSING")
    return t("emailProcessConfigurations.processedEmails.statuses.processing");
  if (status === "PROCESSED")
    return t("emailProcessConfigurations.processedEmails.statuses.processed");
  if (status === "PUBLISH_FAILED")
    return t(
      "emailProcessConfigurations.processedEmails.statuses.publishFailed",
    );
  if (status === "PROCESS_FAILED")
    return t(
      "emailProcessConfigurations.processedEmails.statuses.processFailed",
    );
  return status || "-";
}

function isFailureProcessedEmailStatus(status?: string) {
  return status === "PUBLISH_FAILED" || status === "PROCESS_FAILED";
}

function resolveFailureDetail(email?: ProcessedEmailItem | null) {
  const normalizedError = (email?.error || "").toLowerCase();
  if (
    /(authentication|authenticate|invalid credentials|invalid token|login failed|unauthorized|auth failed|535|534|401)/i.test(
      normalizedError,
    )
  ) {
    return {
      titleKey:
        "emailProcessConfigurations.processedEmails.failureDetails.invalidMailboxToken.title",
      messageKey:
        "emailProcessConfigurations.processedEmails.failureDetails.invalidMailboxToken.message",
    };
  }
  if (/(nup|404|not found)/i.test(normalizedError)) {
    return {
      titleKey:
        "emailProcessConfigurations.processedEmails.failureDetails.nupFailed.title",
      messageKey:
        "emailProcessConfigurations.processedEmails.failureDetails.nupFailed.message",
    };
  }
  if (
    /(process definition|process.*not found|deployment|not deployed)/i.test(
      normalizedError,
    )
  ) {
    return {
      titleKey:
        "emailProcessConfigurations.processedEmails.failureDetails.processUnavailable.title",
      messageKey:
        "emailProcessConfigurations.processedEmails.failureDetails.processUnavailable.message",
    };
  }
  if (
    /(timeout|timed out|connection refused|unavailable|503|502|504)/i.test(
      normalizedError,
    )
  ) {
    return {
      titleKey:
        "emailProcessConfigurations.processedEmails.failureDetails.serviceUnavailable.title",
      messageKey:
        "emailProcessConfigurations.processedEmails.failureDetails.serviceUnavailable.message",
    };
  }
  if (email?.status === "PUBLISH_FAILED") {
    return {
      titleKey:
        "emailProcessConfigurations.processedEmails.failureDetails.publishFailed.title",
      messageKey:
        "emailProcessConfigurations.processedEmails.failureDetails.publishFailed.message",
    };
  }
  if (email?.status === "PROCESS_FAILED") {
    return {
      titleKey:
        "emailProcessConfigurations.processedEmails.failureDetails.processFailed.title",
      messageKey:
        "emailProcessConfigurations.processedEmails.failureDetails.processFailed.message",
    };
  }
  return {
    titleKey:
      "emailProcessConfigurations.processedEmails.failureDetails.generic.title",
    messageKey:
      "emailProcessConfigurations.processedEmails.failureDetails.generic.message",
  };
}

function getProcessedEmailStatusChipSx(status?: string) {
  if (status === "PROCESSED" || status === "FINALIZADO") {
    return {
      bgcolor: "success.light",
      color: "success.dark",
      border: "none",
    };
  }
  if (status === "PUBLISH_FAILED" || status === "PROCESS_FAILED") {
    return {
      bgcolor: "error.light",
      color: "error.dark",
      border: "none",
    };
  }
  return {
    bgcolor: "grey.100",
    color: "text.primary",
    border: "none",
  };
}

function getErrorMessage(error: unknown, fallback: string) {
  const candidate = error as {
    response?: {
      data?: {
        message?: string;
        detailedMessage?: string;
        error_description?: string;
        objects?: Array<{ userMessage?: string; message?: string }>;
      };
    };
    body?: {
      message?: string;
      detailedMessage?: string;
      error_description?: string;
      objects?: Array<{ userMessage?: string; message?: string }>;
    };
    message?: string;
  };
  const data = candidate.response?.data || candidate.body;
  const objectMessage = data?.objects?.find(
    (object) => object?.userMessage || object?.message,
  );
  return (
    objectMessage?.userMessage ||
    objectMessage?.message ||
    data?.message ||
    data?.detailedMessage ||
    data?.error_description ||
    candidate.message ||
    fallback
  );
}

function formatDateTime(value?: string) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}
